import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, filter, take } from 'rxjs/operators';
import { COMMUNITY_HEADER, forgetSignedInMember } from 'src/app/core/services/co-brand.service';
import { ProcessingService } from 'src/app/core/services/processing.service';
import { LoggerService } from 'src/app/shared/services/logger.service';
import type { UserCommunityUpdatedPayload } from 'src/app/shared/services/socket.service';

/** communityViewGuard's answer to a call from a community view the user is no longer in. */
export const NOT_A_MEMBER_MESSAGE = 'You are not a member of this community.';

/** One-shot message shown after the page loads again in the right view. */
export const COMMUNITY_NOTICE_STORAGE_KEY = 'rw:community-notice';
/** When this tab last reloaded because a call was refused, so a refusal can never loop. */
export const COMMUNITY_RELOAD_STORAGE_KEY = 'rw:community-reload-at';
const RELOAD_GUARD_MS = 60_000;

/**
 * A user's community changed while their tab is open: the RealWorld team moved or removed them
 * (USER_COMMUNITY_UPDATED), or a community view they are no longer in got a call refused (a
 * missed socket event, a disabled community). Either way the page is stale: every call from a
 * community view they left is refused, and a members-only deal on screen may no longer be theirs.
 *
 * The page is loaded again, as CoBrandService.openCommunity does: at startup the community
 * comes from GET /auth/me, so a page opened with ?community_id= lands in their new community or on
 * RealWorld.fi, and a members-only deal they lost shows its private-to-community notice. The
 * message is shown after the load. A transaction in progress is never cut off: the load waits for
 * ProcessingService, and the message is shown at once in the meantime.
 *
 * Injected by ErrorInterceptor, so nothing here may depend on HttpClient.
 */
@Injectable({ providedIn: 'root' })
export class CommunityMembershipService {
    private leaving = false;

    constructor(
        private readonly toastr: ToastrService,
        private readonly processingService: ProcessingService,
        private readonly logger: LoggerService
    ) {}

    /**
     * The RealWorld team changed the signed-in user's community.
     * @param {UserCommunityUpdatedPayload} payload the socket event
     * @returns {void}
     */
    communityChanged(payload: UserCommunityUpdatedPayload): void {
        const now = payload.community_id
            ? `You are now a member of ${payload.community_name || 'a new community'}.`
            : `You are no longer a member of ${payload.previous_community_name || 'your community'}.`;
        this.leave(`Your community membership was changed by the RealWorld team. ${now}`);
    }

    /**
     * Whether a failed call is the community view being refused: a 400 "You are not a member of
     * this community." to a call that carried the Community-Id header.
     * @param {HttpRequest<unknown>} req the request
     * @param {HttpErrorResponse} error its error
     * @returns {boolean} true when the user is no longer in the community this page shows
     */
    isMembershipRefusal(req: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
        return error?.status === 400 && req.headers.has(COMMUNITY_HEADER) && error.error?.message === NOT_A_MEMBER_MESSAGE;
    }

    /**
     * A call from this community view was refused. Loads the page again in the right view, at most
     * once a minute: if the API still refuses right after a load, the page stops sending
     * Community-Id instead, so it can never loop.
     * @returns {void}
     */
    membershipRefused(): void {
        if (this.leaving) return;
        if (this.reloadedRecently()) {
            this.logger.warn('Community view refused again right after a reload; leaving it in place');
            forgetSignedInMember();
            this.toastr.error(NOT_A_MEMBER_MESSAGE);
            return;
        }
        this.leave(NOT_A_MEMBER_MESSAGE);
    }

    /**
     * Shows the message saved before the last load, once. Called by app.component at startup.
     * @returns {void}
     */
    showPendingNotice(): void {
        let notice: string | null = null;
        try {
            notice = sessionStorage.getItem(COMMUNITY_NOTICE_STORAGE_KEY);
            sessionStorage.removeItem(COMMUNITY_NOTICE_STORAGE_KEY);
        } catch {
            return;
        }
        if (notice) this.toastr.info(notice);
    }

    /**
     * Saves the message for after the load, then loads the page again once no transaction is in
     * progress. While one is, the message shows at once so the user knows what is coming.
     * @param {string} message what to tell the user
     * @returns {void}
     */
    private leave(message: string): void {
        if (this.leaving) return;
        this.leaving = true;
        let saved = false;
        try {
            sessionStorage.setItem(COMMUNITY_NOTICE_STORAGE_KEY, message);
            sessionStorage.setItem(COMMUNITY_RELOAD_STORAGE_KEY, String(Date.now()));
            saved = sessionStorage.getItem(COMMUNITY_NOTICE_STORAGE_KEY) === message;
        } catch {
            saved = false;
        }
        if (!saved) {
            // storage blocked: no reload guard either, so leave the view in place rather than risk a loop
            forgetSignedInMember();
            this.toastr.info(message);
            return;
        }

        this.processingService.isProcessing$
            .pipe(
                debounceTime(50),
                filter((isProcessing) => {
                    if (isProcessing) this.toastr.info(message);
                    return !isProcessing;
                }),
                take(1)
            )
            .subscribe(() => this.reload());
    }

    /** Separate so specs can stub the page load. */
    reload(): void {
        window.location.reload();
    }

    private reloadedRecently(): boolean {
        try {
            const at = Number(sessionStorage.getItem(COMMUNITY_RELOAD_STORAGE_KEY) || 0);
            return at > 0 && Date.now() - at < RELOAD_GUARD_MS;
        } catch {
            // storage unreadable: assume it just happened so we can never loop
            return true;
        }
    }
}
